import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { WebsiteContent } from '@/types/website';
import { WEBSITE_LABELS, CONTENT_STATUS_LABELS } from '@/types/website';
import { cn } from '@/lib/utils';

interface ContentCardProps {
  content: WebsiteContent;
  onClick?: () => void;
}

const statusColors: Record<string, string> = {
  concept: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  review: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  live: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const websiteColors: Record<string, string> = {
  greenvision: 'border-l-green-500',
  gv_charging: 'border-l-blue-500',
};

export function ContentCard({ content, onClick }: ContentCardProps) {
  const editor = content.editor_profile || content.created_profile;
  
  return (
    <Card
      className={cn(
        'cursor-pointer hover:shadow-md transition-shadow border-l-4',
        websiteColors[content.website]
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-muted rounded-lg shrink-0">
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-medium text-sm line-clamp-1">{content.title}</h3>
              <Badge variant="outline" className={cn('text-xs shrink-0', statusColors[content.status])}>
                {CONTENT_STATUS_LABELS[content.status]}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-2">/{content.slug}</p>
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                {WEBSITE_LABELS[content.website]}
              </Badge>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {format(new Date(content.updated_at), 'd MMM', { locale: nl })}
                {editor && (
                  <span className="ml-1">
                    door {editor.display_name?.split(' ')[0] || 'onbekend'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
